/**
 * @file     RelatedLinks.tsx
 * @purpose  Hub related-links wall: a hairline matrix of plain text links in
 *           auto-filling columns, each with an optional short figure (e.g.
 *           "412 for sale"). Crawlable anchors only, no cards. A figure that is
 *           empty or a placeholder is dropped. Plain elements and a CSS module
 *           (no MUI), so the wall costs nothing to hydrate; a client module
 *           only for the BookCallLink offer.
 */
'use client';

import Link from 'next/link';
import type { CSSProperties } from 'react';
import { BookCallLink } from '@/components/prompts/BookCallLink';
import { SectionHeader } from './SectionHeader';
import { isDisplayableFigure } from './hubKitStyles';
import styles from './RelatedLinks.module.css';

export interface RelatedLinkItem {
  label: string;
  href: string;
  /** Short figure under the label, e.g. "412 for sale" or 412. */
  figure?: string | number | null;
}

export interface RelatedLinksProps {
  items: RelatedLinkItem[];
  /** Section heading. With titleVariant 'label' it renders as a quiet micro-label heading. */
  title?: string;
  titleVariant?: 'display' | 'label';
  eyebrow?: string;
  description?: string;
  /** Narrowest column before the matrix wraps. */
  minColumnWidth?: number;
  id?: string;
}

function ArrowIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={styles.icon}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M7 17L17 7M9 7h8v8" />
    </svg>
  );
}

export function RelatedLinks({
  items,
  title,
  titleVariant = 'display',
  eyebrow,
  description,
  minColumnWidth = 230,
  id,
}: RelatedLinksProps) {
  if (items.length === 0) return null;
  const headingId = id ? `${id}-heading` : undefined;

  return (
    <section id={id} aria-labelledby={title ? headingId : undefined} className={styles.section}>
      {title && titleVariant === 'display' ? (
        <SectionHeader title={title} eyebrow={eyebrow} description={description} id={headingId} />
      ) : null}
      {title && titleVariant === 'label' ? (
        <h2 id={headingId} className={styles.label}>
          {title}
        </h2>
      ) : null}
      {/* Signed in with the app on a buyer page: the call offer's one in-page form. */}
      <BookCallLink />
      <ul className={styles.list} style={{ '--rl-min-col': `${minColumnWidth}px` } as CSSProperties}>
        {items.map((item) => {
          const figure = isDisplayableFigure(item.figure) ? item.figure : null;
          return (
            <li key={item.href} className={styles.item}>
              <Link href={item.href} className={styles.link}>
                <span className={styles.name}>{item.label}</span>
                <span className={figure != null ? styles.iconCellTall : styles.iconCell}>
                  <ArrowIcon />
                </span>
                {figure != null ? (
                  <span className={styles.figure}>{typeof figure === 'number' ? figure.toLocaleString('en-CA') : figure}</span>
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
